/**
 * Прайс-лист на конструкции — HTML для печати в PDF.
 *
 * Повторяет бумажный прайс лаборатории («Прайс-лист OrthoTech 2025.pdf»):
 * шапка в три строки, под ней сроки изготовления, дальше группы конструкций
 * заголовочными полосами и таблица с ценами в двух столбцах — за наличные и
 * по договору.
 *
 * Столбцов четыре, как в бумаге: наименование, описание, цена за наличные,
 * цена по договору. Описание берётся из справочника конструкций — там оно
 * правится второй строкой под названием позиции.
 *
 * Позиции с НУЛЕВЫМИ ценами в прайс не попадают. Ноль в обеих ценах у нас
 * означает «не в прайсе» — служебные позиции, которые считаются иначе или
 * не продаются вовсе. Напечатать их значило бы предложить клинике работу
 * задаром.
 */
import { escapeHtml } from './html.js';
import { formatKop } from '../money.js';
import { PRINT_LOGO_PNG } from './logo.js';
/**
 * Столбцы цен листа с учётом «не задано».
 *
 * Оба выключенных — всё равно что оба включённых: прайс без единой цены
 * клинике бесполезен, и такой лист вернее считать недоделанным, чем печатать.
 */
export function priceListColumns(model) {
    const columns = model.columns ?? { cash: true, contract: true };
    return columns.cash || columns.contract ? columns : { cash: true, contract: true };
}
/** Позиции, которые пойдут в печать: без убранных. */
export function visibleRows(section) {
    return section.rows.filter((row) => !row.hidden);
}
export const PRICE_LIST_TITLE = 'Прайс-лист на\nортодонтические конструкции';
export const PRICE_LIST_HEADERS = ['Наименование изделия', 'Описание изделия', 'Цена за наличные', 'Цена по договору'];
/**
 * Сроки изготовления — постоянный текст бумажного прайса.
 *
 * Держится здесь, а не в настройках: за все годы он не менялся ни разу, а
 * поле в настройках, которое никто никогда не открывает, — это ещё одно место,
 * где значение однажды окажется пустым и уедет в документ для клиники.
 * Понадобится изменить — правится эта строка.
 */
export const PRICE_LIST_TERMS = 'Срок изготовления ортодонтических конструкций – 7-10 дней ' +
    '(для функциональных аппаратов и сложных паяных конструкций – 14-21 день)';
/**
 * Шрифт — из бумажного прайса лаборатории: там весь документ, от заголовка до
 * последней цены, набран Bahnschrift Light SemiCondensed.
 *
 * Сам шрифт есть только в Windows, и на сервере, где рисуется PDF, его нет.
 * Поэтому за ним стоят запасные: узкий DejaVu (ставится в образ) и Liberation
 * Sans. Порядок важен — у кого документ откроется на своём компьютере с
 * Windows, увидит ровно тот шрифт, что в исходном прайсе.
 */
const ШРИФТ = "'Bahnschrift Light SemiCondensed', 'Bahnschrift', 'DejaVu Sans Condensed', " +
    "'Liberation Sans', Arial, sans-serif";
const CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; font-family: ${ШРИФТ}; color: #1c1b18; }
  .page { padding: 12mm 14mm; }
  /*
    Логотип — В ПРАВОМ ВЕРХНЕМ УГЛУ, поверх заголовка, как в бумажном прайсе:
    там он вынесен отдельной плавающей картинкой правее текста. Размер тот же,
    105,75 × 84 пункта.
  */
  /*
    Высота шапки задана логотипом (84 пункта), а не текстом: заголовок с датой
    короче, и без этого логотип свисал бы на строку о сроках. Отступа снизу у
    шапки нет по той же причине — иначе под ней зияет пустая полоса в палец
    шириной, оставшаяся от той же высоты.
  */
  .head { position: relative; text-align: center; min-height: 84pt; }
  .head .logo { position: absolute; right: 0; top: 0; width: 105.75pt; height: 84pt; }
  .head .t1 { font-size: 18pt; line-height: 1.2; padding-top: 6pt; }
  .head .t2 { font-size: 12pt; margin-top: 2mm; }
  .terms { font-size: 10pt; text-align: center; margin-bottom: 6mm; font-style: italic; }
  .part { font-size: 13pt; font-weight: bold; margin: 6mm 0 2mm; }
  table { width: 100%; border-collapse: collapse; }
  /*
    Заголовок таблицы повторяется на каждой странице: прайс не помещается
    на один лист, и без повтора со второй страницы идут числа без объяснения,
    какое из них за наличные, а какое по договору.
  */
  thead { display: table-header-group; }
  th, td { border: 1px solid #8a8578; padding: 1.6mm 2.4mm; font-size: 10.5pt; }
  /*
    Шапка таблицы — ЧЁРНАЯ, белым по чёрному.

    Тем же чёрным, что и логотип: документ держится на двух чёрных пятнах —
    круг вверху справа и полоса заголовков, — и они перекликаются. Рамка у
    шапки тоже чёрная, иначе серая линия по краю дробила бы полосу и та
    переставала читаться цельной.

    Полосы групп и рамки строк оставлены светлыми намеренно. Групп в прайсе
    полтора десятка; будь они тоже чёрными, лист покрылся бы чёрными
    перекладинами, и шапка перестала бы выделяться среди них.
  */
  th { background: #000; border-color: #000; color: #fff; font-weight: bold; text-align: center; }
  td.name { text-align: left; }
  /*
    Описание — помельче и серым: это пояснение к названию, а не равная ему
    величина. Ширина не задана: пусть забирает остаток строки, названия и
    цены важнее и должны стоять как стоят.
  */
  td.desc { text-align: left; font-size: 9.5pt; color: #55524b; }
  td.price { text-align: right; white-space: nowrap; width: 30mm; }
  /* Строка группы: та же полоса, что в бумажном прайсе. */
  td.section { background: #f6f3ec; font-weight: bold; text-align: left; }
  /* Строку не рвём между страницами: цена не должна оторваться от названия. */
  tr { page-break-inside: avoid; }
  .foot { margin-top: 6mm; font-size: 9.5pt; color: #55524b; text-align: center; }
`;
/**
 * Строки таблицы одной части прайса: полосы групп вперемешку с позициями.
 *
 * Группа, у которой убраны все позиции, не печатается вовсе: пустая полоса с
 * названием выглядела бы как забытая, а не как убранная намеренно.
 */
function renderRows(sections, columns) {
    const ширина = 2 + Number(columns.cash) + Number(columns.contract);
    return sections
        .filter((section) => visibleRows(section).length > 0)
        .map((section) => `<tr><td class="section" colspan="${ширина}">${escapeHtml(section.title)}</td></tr>` +
        visibleRows(section)
            .map((row) => '<tr>' +
            `<td class="name">${escapeHtml(row.name)}</td>` +
            `<td class="desc">${escapeHtml(row.description)}</td>` +
            (columns.cash ? `<td class="price">${formatKop(row.priceCashKop)} р.</td>` : '') +
            (columns.contract ? `<td class="price">${formatKop(row.priceContractKop)} р.</td>` : '') +
            '</tr>')
            .join(''))
        .join('');
}
/** Самодостаточная HTML-страница прайса: для PDF и для просмотра. */
export function renderPriceListPage(model) {
    const columns = priceListColumns(model);
    const все = model.headers?.length === 4 ? model.headers : PRICE_LIST_HEADERS;
    /* Названия столбцов — только тех, что печатаются: третья и четвёртая — цены. */
    const заголовки = все.filter((_, i) => (i === 2 ? columns.cash : i === 3 ? columns.contract : true));
    const заголовок = escapeHtml(model.title ?? PRICE_LIST_TITLE).replace(/\n/g, '<br>');
    const части = model.parts
        .filter((part) => part.sections.some((section) => visibleRows(section).length > 0))
        .map((part) => `<div class="part">${escapeHtml(part.title)}</div>` +
        '<table>' +
        '<thead><tr>' +
        заголовки.map((h) => `<th>${escapeHtml(h)}</th>`).join('') +
        '</tr></thead>' +
        `<tbody>${renderRows(part.sections, columns)}</tbody>` +
        '</table>')
        .join('');
    return `<!DOCTYPE html>
<html lang="ru">
<head>
<meta charset="utf-8">
<title>Прайс-лист на ортодонтические конструкции</title>
<style>${CSS}</style>
</head>
<body>
<div class="page">
  <div class="head">
    <img class="logo" src="${PRINT_LOGO_PNG}" alt="">
    <div class="t1">${заголовок}</div>
    <div class="t2">от ${escapeHtml(model.date)} г.</div>
  </div>
  <div class="terms">${escapeHtml(model.terms ?? PRICE_LIST_TERMS)}</div>
  ${части}
  ${model.performerName.trim() ? `<div class="foot">${escapeHtml(model.performerName)}</div>` : ''}
</div>
</body>
</html>`;
}
//# sourceMappingURL=price-list.js.map